/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-array-index-key */
/* eslint-disable import/no-extraneous-dependencies */
import React, { ReactNode, useMemo, useState, useEffect, useRef } from 'react';
import Model from '@tripian/model';
import { ChevronLeft, ChevronRight, NoImage, Star } from '../base/Svg/Icons';
import classes from './TourCard.scss';

type TourCardProps = {
  title: string;
  price?: number;
  currency?: string;
  rating?: number;
  reviewCount?: number;
  images: string[];
  tags: string[];
  provider?: string;
  onBook?: () => void;
  t: (value: Model.TranslationKey) => string;
};

enum ViatorTag {
  FREE_CANCELLATION = 'Free Cancellation',
  LIKELY_TO_SELL_OUT = 'Likely To Sell Out',
  NEW_ON_VIATOR = 'New On Viator',
  VIATOR_EXCLUSIVE = 'Viator Exclusive',
  SKIP_THE_LINE = 'Skip The Line',
  PRIVATE_TOUR = 'Private Tour',
  SPECIAL_OFFER = 'Special Offer',
}

const getProviderLogo = (provider?: string): ReactNode => {
  const providerNameClasses = 'hidden md:block text-sm text-[#696969]';

  switch (provider) {
    case 'GetYourGuide':
      return (
        <div className="flex items-center gap-2">
          <span className={`${classes.logo} ${classes.gyg}`} />
          <span className={providerNameClasses}>GetYourGuide</span>
        </div>
      );
    case 'Viator':
      return (
        <div className="flex items-center gap-2">
          <span className={`${classes.logo} ${classes.viator}`} />
          <span className={providerNameClasses}>Viator</span>
        </div>
      );

    case 'GoCity':
      return (
        <div className="flex items-center gap-2">
          <span className={`${classes.logo} ${classes.gocity}`} />
        </div>
      );

    case 'Victory':
      return (
        <div className="flex items-center gap-2">
          <span className={`${classes.logo} ${classes.victory}`} />
          <span className={providerNameClasses}>Victory Live</span>
        </div>
      );
    case 'Toristy':
      return (
        <div className="flex items-center gap-2">
          <span className={`${classes.logo} ${classes.toristy}`} />
          <span className={providerNameClasses}>Toristy</span>
        </div>
      );
    case 'BookBarbados':
      return (
        <div className="flex items-center gap-2">
          <span className={`${classes.logo} ${classes.bb}`} />
          <span className={providerNameClasses}>BookBarbados</span>
        </div>
      );
    case 'Rezdy':
      return (
        <div className="flex items-center gap-2">
          <span className={`${classes.logo} ${classes.rezdy}`} />
        </div>
      );
    case 'OccasionGenius':
      return (
        <div className="flex items-center gap-2">
          <span className={`${classes.logo} ${classes.occasiongenius}`} />
        </div>
      );
    default:
      return null;
  }
};

const translateTagForViator = (tag: string, t: (value: Model.TranslationKey) => string): string => {
  switch (tag) {
    case ViatorTag.FREE_CANCELLATION:
      return t('trips.myTrips.localExperiences.tourDetails.freeCancellation');
    case ViatorTag.LIKELY_TO_SELL_OUT:
      return t('trips.myTrips.localExperiences.tourDetails.likelyToSellOut');
    case ViatorTag.NEW_ON_VIATOR:
      return t('trips.myTrips.localExperiences.tourDetails.newOnViator');
    case ViatorTag.VIATOR_EXCLUSIVE:
      return t('trips.myTrips.localExperiences.tourDetails.viatorExclusive');
    case ViatorTag.SKIP_THE_LINE:
      return t('trips.myTrips.localExperiences.tourDetails.skipTheLine');
    case ViatorTag.PRIVATE_TOUR:
      return t('trips.myTrips.localExperiences.tourDetails.privateTour');
    case ViatorTag.SPECIAL_OFFER:
      return t('trips.myTrips.localExperiences.tourDetails.specialOffer');
    default:
      return tag;
  }
};

const TourCard: React.FC<TourCardProps> = ({ title, price, currency = '$', rating, reviewCount, images, tags, provider, onBook, t }) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

  const currentImageUrl = images.length > 0 ? images[activeSlideIndex] : '';

  useEffect(() => {
    if (imageRef.current) {
      const img = new Image();
      img.src = currentImageUrl;

      if (img.complete) {
        setIsImageLoading(false);
      } else {
        setIsImageLoading(true);
        img.onload = () => {
          setIsImageLoading(false);
        };
      }
    }
  }, [currentImageUrl]);

  const priceSymbol = useMemo(() => {
    if (currency === 'USD') return '$';
    if (currency === 'EUR') return '€';
    return `${currency || ''} `;
  }, [currency]);

  const nextSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsImageLoading(true);
    setActiveSlideIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsImageLoading(true);
    setActiveSlideIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToSlide = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setIsImageLoading(true);
    setActiveSlideIndex(index);
  };

  return (
    <div
      className="group mb-4 flex flex-row overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-md cursor-pointer md:cursor-default"
      onKeyDown={() => {}}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        if (isMobile && onBook) onBook();
      }}
    >
      <div className="relative h-[120px] sm:h-[130px] md:h-[140px] lg:h-[180px] w-[120px] sm:w-[140px] md:w-[180px] lg:w-[260px] flex-shrink-0 overflow-hidden">
        <div className="relative w-full h-full">
          {currentImageUrl ? (
            <div className="relative w-full h-full">
              <img ref={imageRef} src={currentImageUrl} alt={title} className={`h-full w-full object-cover transition-all duration-300 ease-in-out rounded-r-2xv ${isImageLoading ? 'opacity-40' : 'opacity-100'}`} loading="lazy" />
              {isImageLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/10">
                  <div className="w-10 h-10 border-[3px] border-white border-t-transparent rounded-full animate-spin shadow-md" />
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={prevSlide} type="button" className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white bg-opacity-70 shadow-sm flex items-center justify-center z-10 hover:bg-opacity-90 transition-all">
                    <ChevronLeft className="w-4 h-4 text-gray-700" />
                  </button>
                  <button onClick={nextSlide} type="button" className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white bg-opacity-70 shadow-sm flex items-center justify-center z-10 hover:bg-opacity-90 transition-all">
                    <ChevronRight className="w-4 h-4 text-gray-700" />
                  </button>
                </>
              )}
              {images.length > 1 && (
                <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1 z-10">
                  {images.map((_, index) => (
                    <button key={`slider-dot-${index}`} type="button" onClick={(e) => goToSlide(e, index)} className={`w-1.5 h-1.5 rounded-full ${index === activeSlideIndex ? 'bg-white' : 'bg-white bg-opacity-50'}`} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="h-full w-full bg-gray-100 flex flex-col items-center justify-center rounded-l-2xl">
              <NoImage className="w-12 h-12 text-gray-400 mb-2" />
              <span className="text-xs text-gray-400">No Image</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-2 sm:p-3 lg:p-4 justify-between overflow-hidden">
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center h-6 overflow-hidden">{getProviderLogo(provider)}</div>
            <div className="flex items-center gap-2">
              {price && (
                <span className="text-sm font-semibold text-gray-900">
                  {priceSymbol} {price.toLocaleString()}
                </span>
              )}
              <button
                className="hidden md:inline-flex items-center justify-center h-6 sm:h-8 px-2 sm:px-4 rounded-full border border-gray-300 bg-primary-color text-white transition-colors min-w-[60px] sm:min-w-[80px]"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onBook?.();
                }}
              >
                <span className="text-xs sm:text-sm font-medium">{t('book')}</span>
              </button>
            </div>
          </div>
          <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-1 lg:mb-2 line-clamp-2">{title}</h3>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {typeof rating === 'number' && rating > 0 && (
              <div className="flex items-center">
                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100">
                  <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{rating.toFixed(1)}</span>
                  <Star className="w-4 h-4 pb-[0.125rem] text-yellow-400" />
                  {reviewCount && <span className="text-xs text-gray-500 whitespace-nowrap">({reviewCount})</span>}
                </div>
              </div>
            )}
            {tags.length > 0 && (
              <div className="flex gap-1 whitespace-nowrap">
                {tags.map((tag, index) => (
                  <span key={index} className="inline-flex flex-shrink-0 items-center px-3 py-1 rounded-full bg-gray-100 text-xs sm:text-sm">
                    {provider === 'Viator' ? translateTagForViator(tag, t) : tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourCard;
